import { useEffect, useState } from "react";
import { ShieldX, ShieldCheck } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { StatusPill } from "@/components/uiext";
import { toast } from "sonner";

/**
 * In-memory cache so a noisy security log full of repeating IPs
 * doesn't fan-out to N HTTP calls per render. Re-fetched per IP at
 * most every CACHE_TTL_MS.
 */
const _CACHE = new Map();
const CACHE_TTL_MS = 4000;

/**
 * Live "is the firewall watching this IP?" cell.
 *
 * @param {string} ip               Source IP from the event row.
 * @param {"tenant"|"platform"} scope  Determines API base.
 * @param {(ip:string)=>void} onQuickBan Called after a successful quick-ban
 *                                       so the parent page can refresh.
 */
export function FirewallStatusCell({ ip, scope = "tenant", onQuickBan }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const base = scope === "platform" ? "/platform/firewall" : "/firewall";

  useEffect(() => {
    let alive = true;
    const fetchIt = async () => {
      const cached = _CACHE.get(ip);
      const now = Date.now();
      if (cached && now - cached.ts < CACHE_TTL_MS) {
        if (alive) setData(cached.data);
        return;
      }
      try {
        const r = await api.get(
          `${base}/ip-status?ip=${encodeURIComponent(ip)}`
        );
        _CACHE.set(ip, { ts: now, data: r.data });
        if (alive) setData(r.data);
      } catch {
        if (alive) setData({ ip, rules: [], cumulative: null, active_ban: null });
      }
    };
    fetchIt();
    return () => {
      alive = false;
    };
  }, [ip, base]);

  if (!data) {
    return <span className="text-[11px] text-muted-foreground">—</span>;
  }

  // If the IP is already banned, show a clear pill and a disabled state.
  if (data.active_ban) {
    return (
      <div className="flex items-center gap-1.5">
        <StatusPill kind="danger">
          <ShieldCheck className="h-3 w-3 mr-1 inline" />
          {t("firewall.cell.banned", "BANNED")}
        </StatusPill>
        <span className="text-[10px] text-muted-foreground" title={data.active_ban.reason}>
          {data.active_ban.actor_scope === "engine" ? "auto" : data.active_ban.actor}
        </span>
      </div>
    );
  }

  // Find the per-event-type rule that's closest to firing
  const peakRule = (data.rules || [])
    .slice()
    .sort((a, b) => b.count / b.max_count - a.count / a.max_count)[0];
  const cum = data.cumulative;

  // No tracking happening at all
  if (!peakRule && (!cum || cum.count === 0)) {
    return <span className="text-[10px] text-muted-foreground">{t("firewall.cell.idle", "idle")}</span>;
  }

  const closest = peakRule
    ? { count: peakRule.count, max: peakRule.max_count, label: peakRule.event_type }
    : null;
  const cumPart = cum && cum.count > 0
    ? { count: cum.count, max: cum.max_count }
    : null;

  // Pick worst-case ratio for the pill colour
  const ratio = Math.max(
    closest ? closest.count / closest.max : 0,
    cumPart ? cumPart.count / cumPart.max : 0
  );
  const kind = ratio >= 0.8 ? "danger" : ratio >= 0.5 ? "warn" : "info";

  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {closest && (
        <StatusPill
          kind={kind}
          data-testid={`fw-cell-rule-${ip}`}
        >
          <span className="font-mono">
            {closest.count}/{closest.max}
          </span>
          <span className="ml-1 text-[10px] opacity-80">{closest.label}</span>
        </StatusPill>
      )}
      {cumPart && (
        <StatusPill kind={cumPart.count >= cumPart.max * 0.6 ? "warn" : "muted"}>
          <span className="font-mono">
            Σ {cumPart.count}/{cumPart.max}
          </span>
        </StatusPill>
      )}
      <Button
        size="sm"
        variant="ghost"
        className="h-6 px-1.5 text-[10px] text-[hsl(var(--danger))] hover:bg-[hsl(var(--danger))]/10 border border-[hsl(var(--danger))]/30 rounded"
        title={t(
          "firewall.cell.quickBanTitle",
          "Click to manually ban this IP for 1 hour. (This is an action, not a status — the IP is still active right now.)"
        )}
        data-testid={`fw-cell-quickban-${ip}`}
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          try {
            await api.post(`${base}/bans`, {
              ip,
              reason: t("firewall.cell.quickBanReason", "manual quick-ban from Security page"),
              preset: "1h",
            });
            _CACHE.delete(ip);
            toast.success(t("firewall.cell.quickBanned", "Banned {{ip}} for 1h", { ip }));
            if (onQuickBan) onQuickBan(ip);
          } catch (e) {
            toast.error(e?.response?.data?.detail || t("common.failed", "Failed"));
          } finally {
            setBusy(false);
          }
        }}
      >
        <ShieldX className="h-3 w-3 mr-0.5" />
        {t("firewall.cell.banNow", "Ban now")}
      </Button>
    </div>
  );
}

/**
 * Small helper hooked into the security pages to bust the cache after
 * a manual ban (so the cell flips to "BANNED" immediately).
 */
export function clearFirewallStatusCache(ip) {
  if (ip) _CACHE.delete(ip);
  else _CACHE.clear();
}

export default FirewallStatusCell;
